import { cutoutUrl } from '../commerce/cutouts';
import { getProductRepository } from '../commerce/product.repository';
import { createAisle } from '../components/aisle/aisle';
import { createShelf } from '../components/shelf/shelf';
import { applyBounds, createHotspotElement } from '../interactions/hotspot';
import { prefersReducedMotion } from './scene.transitions';
import type { ShopProduct } from '../commerce/shopify.types';
import type { SceneDefinition, SceneHotspot } from './scene.types';

/**
 * Pose la vraie photo de chaque produit à l'emplacement de son hotspot.
 * En attendant les illustrations définitives, c'est ce qui rend le rayon lisible.
 * Le catalogue est interrogé ici plutôt que passé en paramètre pour que le
 * renderer reste utilisable tel quel par n'importe quelle scène.
 */
async function decorateProducts(
  definition: SceneDefinition,
  layer: HTMLElement,
  images: Map<string, HTMLImageElement>,
  onPlaced: () => void,
): Promise<void> {
  const productHotspots = definition.hotspots.filter(
    (hotspot) => hotspot.action === 'product' && hotspot.target,
  );
  if (productHotspots.length === 0) return;

  let products;
  try {
    products = await getProductRepository().getProductsByHandles(
      productHotspots.map((hotspot) => hotspot.target as string),
    );
  } catch {
    // Catalogue injoignable : le décor reste nu, la visite continue.
    return;
  }

  for (const hotspot of productHotspots) {
    const product = products.find((candidate) => candidate.handle === hotspot.target);
    if (!product?.imageUrl) continue;

    const image = document.createElement('img');
    image.className = 'scene__product';
    image.src = cutoutUrl(product.handle, product.imageUrl);
    image.alt = '';
    image.setAttribute('aria-hidden', 'true');
    image.loading = 'lazy';
    image.decoding = 'async';
    image.dataset['handle'] = product.handle;
    if (!product.availableForSale) image.classList.add('is-unavailable');
    image.addEventListener('error', () => image.remove());

    applyBounds(image, hotspot, window.innerWidth);
    layer.append(image);
    images.set(hotspot.id, image);
  }

  onPlaced();
}

/**
 * Garnit les emplacements de mise en avant avec les meilleures ventes du
 * moment. Chaque produit devient un hotspot comme les autres : fiche, survol
 * et analytics fonctionnent sans code particulier.
 */
async function decorateFeatured(
  definition: SceneDefinition,
  productLayer: HTMLElement,
  hotspotLayer: HTMLElement,
  hotspotElements: Map<string, HTMLButtonElement>,
  handlers: SceneHandlers,
): Promise<void> {
  const positions = definition.featured?.positions ?? [];
  if (positions.length === 0) return;

  let produits: ShopProduct[];
  try {
    produits = await getProductRepository().getFeaturedProducts();
  } catch {
    return;
  }

  positions.forEach((bounds, index) => {
    const produit = produits[index];
    if (!produit) return;

    const hotspot: SceneHotspot = {
      id: `${definition.id}.featured-${index}`,
      label: `Regarder ${produit.title}, meilleure vente`,
      action: 'product',
      target: produit.handle,
      hint: 'Meilleure vente',
      simplifiedLabel: `Voir ${produit.title}`,
      ...bounds,
    };

    const button = createHotspotElement(hotspot, (activated) =>
      handlers.onHotspot(activated, button),
    );
    button.addEventListener('pointerenter', () => handlers.onProductHover?.(produit, button));
    button.addEventListener('focus', () => handlers.onProductHover?.(produit, button));
    button.addEventListener('pointerleave', () => handlers.onProductLeave?.());
    button.addEventListener('blur', () => handlers.onProductLeave?.());
    hotspotElements.set(hotspot.id, button);
    hotspotLayer.append(button);

    if (!produit.imageUrl) return;
    const image = document.createElement('img');
    image.className = 'scene__product';
    image.src = cutoutUrl(produit.handle, produit.imageUrl);
    image.alt = '';
    image.setAttribute('aria-hidden', 'true');
    image.loading = 'lazy';
    image.dataset['handle'] = produit.handle;
    applyBounds(image, hotspot, window.innerWidth);
    productLayer.append(image);
  });
}

interface PanControls {
  element: HTMLElement;
  destroy(): void;
}

/**
 * Déplacement latéral quand le décor est plus large que l'écran : flèches,
 * glisser à la souris, molette, et défilement tactile natif. Les contrôles
 * disparaissent dès que tout le décor tient à l'écran.
 */
function createPanControls(viewport: HTMLElement): PanControls {
  const element = document.createElement('div');
  element.className = 'scene__pan';
  element.hidden = true;

  const left = document.createElement('button');
  left.type = 'button';
  left.className = 'scene__pan-button scene__pan-button--left';
  left.setAttribute('aria-label', 'Regarder vers la gauche');
  left.textContent = '‹';

  const right = document.createElement('button');
  right.type = 'button';
  right.className = 'scene__pan-button scene__pan-button--right';
  right.setAttribute('aria-label', 'Regarder vers la droite');
  right.textContent = '›';

  const rail = document.createElement('div');
  rail.className = 'scene__pan-rail';
  const thumb = document.createElement('div');
  thumb.className = 'scene__pan-thumb';
  rail.append(thumb);

  element.append(left, rail, right);

  function overflow(): number {
    return viewport.scrollWidth - viewport.clientWidth;
  }

  function refresh(): void {
    const max = overflow();
    element.hidden = max < 8;
    if (element.hidden) return;
    const ratio = viewport.clientWidth / viewport.scrollWidth;
    const progress = viewport.scrollLeft / max;
    thumb.style.width = `${(ratio * 100).toFixed(1)}%`;
    thumb.style.left = `${(progress * (100 - ratio * 100)).toFixed(1)}%`;
    left.disabled = viewport.scrollLeft <= 2;
    right.disabled = viewport.scrollLeft >= max - 2;
  }

  const step = (direction: number): void => {
    viewport.scrollBy({ left: direction * viewport.clientWidth * 0.7, behavior: 'smooth' });
  };
  left.addEventListener('click', () => step(-1));
  right.addEventListener('click', () => step(1));

  // Glisser-déposer à la souris : le décor se parcourt comme une photo panoramique.
  let dragging = false;
  let startX = 0;
  let startScroll = 0;
  const onPointerDown = (event: PointerEvent): void => {
    if (event.pointerType !== 'mouse' || overflow() < 8) return;
    if ((event.target as HTMLElement).closest('button, a')) return;
    dragging = true;
    startX = event.clientX;
    startScroll = viewport.scrollLeft;
    viewport.classList.add('is-dragging');
  };
  const onPointerMove = (event: PointerEvent): void => {
    if (!dragging) return;
    viewport.scrollLeft = startScroll - (event.clientX - startX);
  };
  const stopDrag = (): void => {
    dragging = false;
    viewport.classList.remove('is-dragging');
  };

  viewport.addEventListener('pointerdown', onPointerDown);
  window.addEventListener('pointermove', onPointerMove);
  window.addEventListener('pointerup', stopDrag);
  viewport.addEventListener('scroll', refresh, { passive: true });
  window.addEventListener('resize', refresh);

  // Deux frames de battement : le décor doit être mis en page avant la mesure.
  requestAnimationFrame(() => requestAnimationFrame(refresh));

  return {
    element,
    destroy(): void {
      viewport.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', stopDrag);
      viewport.removeEventListener('scroll', refresh);
      window.removeEventListener('resize', refresh);
      element.remove();
    },
  };
}

export interface RenderedScene {
  element: HTMLElement;
  hotspotElements: Map<string, HTMLButtonElement>;
  destroy(): void;
}

/**
 * Rendu déclaratif : la scène vient des données, jamais d'un DOM écrit à la main.
 * Chaque calque reçoit sa profondeur pour l'effet 2.5D (cf. §9).
 */
export interface SceneHandlers {
  /** `origine` est la vignette cliquée, pour l'animation de sortie d'étagère. */
  onHotspot: (hotspot: SceneHotspot, origine?: HTMLElement) => void;
  onProductHover?: (product: ShopProduct, anchor: HTMLElement) => void;
  onProductLeave?: () => void;
}

export function renderScene(
  definition: SceneDefinition,
  handlers: SceneHandlers,
): RenderedScene {
  const onHotspot = handlers.onHotspot;
  const element = document.createElement('section');
  element.className = 'scene';
  element.dataset['sceneId'] = definition.id;
  element.setAttribute('aria-label', definition.title);

  /*
   * Le décor garde toujours son cadrage d'origine : la scène fait exactement
   * 16:9 et se parcourt horizontalement quand l'écran est plus étroit. Sans
   * cela, un téléphone en portrait ne montrait que 31 % de la largeur du décor
   * et la moitié des objets restaient hors champ.
   */
  const viewport = document.createElement('div');
  viewport.className = 'scene__viewport';

  const stage = document.createElement('div');
  stage.className = 'scene__stage';

  // Un panorama se déclare plus large que 16:9 ; c'est ce ratio qui fixe le
  // repère des coordonnées en pourcentage de toute la scène.
  if (definition.aspectRatio) {
    stage.style.aspectRatio = String(definition.aspectRatio);
  }

  if (definition.aisle) {
    // L'allée pose elle-même ses décors : aucun fond de scène unique.
    stage.classList.add('scene__stage--aisle');
  } else if (definition.backgroundRepeat) {
    // Fond répété : une tuile raccordée couvre toute la longueur de l'allée.
    stage.classList.add('scene__stage--tiled');
    stage.style.backgroundImage = `url("${definition.background}")`;
  } else {
    const background = document.createElement('img');
    background.className = 'scene__layer scene__layer--background';
    background.src = definition.background;
    background.alt = '';
    background.setAttribute('aria-hidden', 'true');
    background.decoding = 'async';
    background.style.setProperty('--depth', '0');
    stage.append(background);
  }

  for (const layer of definition.layers) {
    const image = document.createElement('img');
    image.className = 'scene__layer';
    image.src = layer.src;
    image.decoding = 'async';
    image.loading = 'lazy';
    image.dataset['layerId'] = layer.id;
    image.style.setProperty('--depth', String(layer.depth));

    // Un objet posé dans le décor est placé en pourcentage, comme les hotspots ;
    // sans coordonnées, le calque couvre toute la scène.
    if (layer.x !== undefined && layer.y !== undefined && layer.width !== undefined) {
      image.classList.add('scene__layer--prop');
      image.style.left = `${layer.x}%`;
      image.style.top = `${layer.y}%`;
      image.style.width = `${layer.width}%`;
    }
    if (layer.decorative || !layer.alt) {
      image.alt = '';
      image.setAttribute('aria-hidden', 'true');
    } else {
      image.alt = layer.alt;
    }
    // Une image manquante ne doit pas laisser d'icône cassée dans le décor.
    image.addEventListener('error', () => {
      image.classList.add('is-missing');
      image.setAttribute('aria-hidden', 'true');
    });
    stage.append(image);
  }

  // Les photos produits se posent sous les hotspots, jamais par-dessus.
  const productLayer = document.createElement('div');
  productLayer.className = 'scene__products';
  stage.append(productLayer);

  const hotspotLayer = document.createElement('div');
  hotspotLayer.className = 'scene__hotspots';

  const hotspotElements = new Map<string, HTMLButtonElement>();
  for (const hotspot of definition.hotspots) {
    const button = createHotspotElement(hotspot, onHotspot);
    hotspotElements.set(hotspot.id, button);
    hotspotLayer.append(button);
  }

  // Étagère alimentée par le catalogue, si la scène en déclare une.
  const shelf = definition.shelf
    ? createShelf(definition.shelf, {
        onProduct: onHotspot,
        ...(handlers.onProductHover ? { onHover: handlers.onProductHover } : {}),
        ...(handlers.onProductLeave ? { onLeave: handlers.onProductLeave } : {}),
      })
    : null;
  if (shelf) stage.append(shelf.element);

  const aisle = definition.aisle
    ? createAisle(definition.aisle, viewport, {
        onProduct: onHotspot,
        ...(handlers.onProductHover ? { onHover: handlers.onProductHover } : {}),
        ...(handlers.onProductLeave ? { onLeave: handlers.onProductLeave } : {}),
      })
    : null;
  if (aisle) stage.append(aisle.element);

  stage.append(hotspotLayer);
  viewport.append(stage);

  // Tri, pagination et raccourcis restent fixes à l'écran, hors de la zone qui
  // défile. Ils sont posés AVANT le viewport dans le DOM, alors qu'ils sont
  // dessinés par-dessus : ce sont des raccourcis de navigation, ils doivent
  // donc arriver au clavier avant le contenu qu'ils servent à éviter. Les
  // placer après obligeait à traverser tous les produits d'un rayon pour
  // atteindre le bouton qui mène au rayon suivant.
  if (shelf) element.append(shelf.controls);
  if (aisle) element.append(aisle.controls);

  element.append(viewport);

  const pan = createPanControls(viewport);
  element.append(pan.element);

  /*
   * Cadrage à l'arrivée. Sur un écran étroit, le décor est plus large que la
   * fenêtre : commencer au bord gauche montrait un pan de mur, alors que le
   * sujet de chaque pièce, le comptoir, le rayonnage, la porte, est composé au
   * centre. On centre donc la vue dès qu'il y a du débordement, ce qui ne
   * change rien sur un écran large où le décor tient en entier.
   *
   * L'allée fait exception : elle se parcourt d'une vitrine à l'autre et doit
   * s'ouvrir sur la première, pas sur son milieu.
   */
  if (!definition.aisle) {
    requestAnimationFrame(() => {
      const debordement = viewport.scrollWidth - viewport.clientWidth;
      if (debordement > 8) viewport.scrollLeft = debordement / 2;
    });
  }

  // Parallaxe pointeur, desktop uniquement, amplitude volontairement faible.
  const supportsHover = window.matchMedia?.('(hover: hover) and (pointer: fine)').matches ?? false;
  const onPointerMove = (event: PointerEvent): void => {
    const rect = stage.getBoundingClientRect();
    const px = (event.clientX - rect.left) / rect.width - 0.5;
    const py = (event.clientY - rect.top) / rect.height - 0.5;
    stage.style.setProperty('--pointer-x', `${(px * 18).toFixed(2)}px`);
    stage.style.setProperty('--pointer-y', `${(py * 12).toFixed(2)}px`);
  };
  const parallaxEnabled = supportsHover && !prefersReducedMotion();
  if (parallaxEnabled) stage.addEventListener('pointermove', onPointerMove);

  const productImages = new Map<string, HTMLImageElement>();

  const onResize = (): void => {
    for (const hotspot of definition.hotspots) {
      const button = hotspotElements.get(hotspot.id);
      if (button) applyBounds(button, hotspot, window.innerWidth);
      const image = productImages.get(hotspot.id);
      if (image) applyBounds(image, hotspot, window.innerWidth);
    }
  };
  window.addEventListener('resize', onResize);

  // Chargement différé : le décor s'affiche sans attendre le catalogue.
  void decorateProducts(definition, productLayer, productImages, onResize);
  void decorateFeatured(definition, productLayer, hotspotLayer, hotspotElements, handlers);

  return {
    element,
    hotspotElements,
    destroy(): void {
      if (parallaxEnabled) stage.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('resize', onResize);
      shelf?.destroy();
      aisle?.destroy();
      pan.destroy();
      element.remove();
    },
  };
}
